import type {Maybe} from "../../../bootstrap/common/types";
import {GLOBAL} from "../../../common/globals";
import {autoRegister} from "../../../container";


@autoRegister()
export class AudibleTextCollector {
    public determineAudibleTextBlocks(): string[] {
        const audibleElements = Array.from(GLOBAL.bodyElement().querySelectorAll(".audible"))
            .filter(element => !element.parent()!.closest(".audible"));

        const textContainers = audibleElements
            .map(element => ElementWithText.from(element));

        return textContainers
            .flatMap(hasText => hasText.getTextBlocks());
    }
}

interface TextContainer {
    getTextBlocks: () => string[];
    shouldBeUnzipped: boolean;
    append: (other: TextContainer) => TextContainer[];
    appendToText: (previousText: TextNode) => TextContainer[];
    appendToElement: (previousElement: ElementWithText) => TextContainer[];
}

class TextNode implements TextContainer {

    public shouldBeUnzipped = false;

    private constructor(private text: Maybe<string>) {
    }

    public static from(node: Text): TextNode {
        const text = node.textContent?.trim();
        return new TextNode(text);
    }

    public getTextBlocks(): string[] {
        return this.text ? [this.text] : [];
    }

    public append(next: TextContainer): TextContainer[] {
        return next.appendToText(this);
    }

    public appendToText(previous: TextNode): TextContainer[] {
        const mergedText = [...previous.getTextBlocks(), ...this.getTextBlocks()]
            .join(" ");
        return [new TextNode(mergedText)];
    }

    public appendToElement(previous: ElementWithText): TextContainer[] {
        return [previous, this];
    }
}

class ElementWithText implements TextContainer {

    private constructor(private subContainers: TextContainer[], public shouldBeUnzipped: boolean) {
    }

    public static from(element: Element): ElementWithText {
        const subContainers: TextContainer[] = [];
        for (const child of element.childNodes) {
            if (child instanceof Text) {
                subContainers.push(TextNode.from(child));
            }
            if (child instanceof HTMLScriptElement || child instanceof HTMLStyleElement) {
                continue;
            }
            if (child instanceof Element) {
                const childWithText = ElementWithText.from(child);
                if (childWithText.shouldBeUnzipped) {
                    subContainers.push(...childWithText.subContainers);
                } else {
                    subContainers.push(childWithText);
                }
            }
        }

        return new ElementWithText(compress(subContainers), element.classList.contains("read-aloud-inline"));
    }

    public append(next: TextContainer): TextContainer[] {
        return next.appendToElement(this);
    }

    public appendToText(previous: TextNode): TextContainer[] {
        return [previous, this];
    }

    public appendToElement(previous: ElementWithText): TextContainer[] {
        return [previous, this];
    }

    public getTextBlocks(): string[] {
        return this.subContainers.flatMap(item => item.getTextBlocks());
    }
}

function compress(uncompressed: TextContainer[]): TextContainer[] {
    if (uncompressed.isEmpty()) {
        return [];
    }

    const compressed = [uncompressed.first()!];
    for (const current of uncompressed.slice(1)) {
        const last = compressed.pop()!;
        compressed.push(...last.append(current));
    }
    return compressed;
}